import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import EN_US from 'locales/translation.en-US.json';
import { initReactI18next } from 'react-i18next';

import { INQUIRY_LANGUAGES } from 'lib/constants/locales';
import DynamicLoadBackend from 'lib/dynamicLoadBackend';

/**
 * Solely used by convertDetectedLanguage to remap preferred locales to
 * something i18next recognizes. This is needed because the way i18next iterates
 * through and resolves preferred languages is unintuitive.
 */
const FALLBACK_LANGUAGES: Record<string, keyof typeof INQUIRY_LANGUAGES> = {
  ar: 'ar-EG',
  el: 'el-GR',
  en: 'en-US',
  es: 'es-MX',
  id: 'id-ID',
  ko: 'ko-KR',
  nl: 'nl-NL',
  pt: 'pt-BR',
  tr: 'tr-TR',
  uk: 'uk-UA',
  zh: 'zh-CN',
};

export function convertDetectedLanguage(lng: string): string {
  // InquiryLanguage is supported; no conversion needed
  if (lng in INQUIRY_LANGUAGES) return lng;

  // Map to first fallback language. We do this because multiple
  // preferred languages can be provided, and i18next iterates through
  // the list of preferred languages BEFORE evaluating fallbacks,
  // whereas we want to evaluate fallbacks BEFORE iterating down the
  // preferred language list.
  // https://github.com/i18next/i18next/blob/v19.8.4/i18next.js#L1012-L1043
  //
  // Ex. ['zh', 'en-US'] will resolve to English instead of Chinese,
  // despite a fallback existing for zh -> zh-CN.
  //
  // The DEFAULT evaluation order is:
  // 1. zh exact match
  // 2. en-US exact match (matches)
  // 3. zh fallback
  // 4. en-US fallback
  if (lng in FALLBACK_LANGUAGES) return FALLBACK_LANGUAGES[lng];

  // Repeat logic after stripping out locale portion. This lets us resolve supported languages with unsupported locales like es-ES.
  const lngWithoutLocale = lng.split('-')[0];
  if (lngWithoutLocale in INQUIRY_LANGUAGES) return lngWithoutLocale;
  if (lngWithoutLocale in FALLBACK_LANGUAGES) return FALLBACK_LANGUAGES[lngWithoutLocale];

  // Otherwise, just return original value. i18next's fallback system will resolve to English.
  return lng;
}

// set html lang attribute for chrome lighthouse accessibility checks
// this event fires on initial load as well as i18n.changeLanguage calls
i18next.on('languageChanged', (lng) => (document.documentElement.lang = lng));

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(DynamicLoadBackend)
  .init({
    // TODO: Update to v4
    // https://www.i18next.com/misc/json-format#i18next-json-v3
    // *_plural is no longer a valid suffix. This appears to be the only pluralization suffix we use.
    compatibilityJSON: 'v3',
    // We do not use i18next's fallback system, because i18next will already
    // resolve ar to ar-EG when it moves to the second phase of best match
    // language resolution (strips locale). We should only use this if we want
    // to map languages to OTHER languages (e.g. make French Canadian fall back
    // to English or something).
    fallbackLng: {
      default: ['en-US'],
    },
    resources: {
      'en-US': EN_US,
    },
    // Allows us to combine eager and dynamically loading resources
    partialBundledLanguages: true,

    detection: {
      lookupQuerystring: 'language',
      convertDetectedLanguage,
    },

    // Limit loading and resolution to existing languages
    load: 'currentOnly',
    supportedLngs: Object.keys(INQUIRY_LANGUAGES),

    // React already does escaping
    interpolation: { escapeValue: false },

    react: {
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      nsMode: 'default',
      useSuspense: false,
    },

    debug: import.meta.env.DEV,
  });
